import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';

import CircularProgress from '@mui/material/CircularProgress';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { AuditLog as AuditLogType, AuditLogFilters } from '../../../../shared/types/audit.types';
import useAuditLog, { AuditLogCursor } from '../../wrappers/use-audit-log';
import useUser from '../../wrappers/use-user';
import { COLLECTIONS } from '../../../../shared/constants/collections';
import { auditLogStyles as sx } from './AuditLog.styles';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 50;

const OPERATION_COLORS: Record<string, string> = {
    CREATE: 'success.main',
    UPDATE: 'warning.main',
    DELETE: 'error.main',
};

interface PendingFilters {
    entityType: string;
    entityId: string;
    actorUid: string;
    dateFrom: string;
    dateTo: string;
}

const EMPTY_PENDING: PendingFilters = {
    entityType: '',
    entityId: '',
    actorUid: '',
    dateFrom: '',
    dateTo: '',
};

const JsonBlock: React.FC<{ data: Record<string, unknown> }> = ({ data }) => (
    <Box component="pre" sx={sx.jsonBlock}>
        {JSON.stringify(data, null, 2)}
    </Box>
);

const AuditLog: React.FC = () => {
    const { t } = useTranslation();
    const { getList } = useAuditLog();
    const { get: getUser } = useUser();

    const [pending, setPending] = useState<PendingFilters>(EMPTY_PENDING);
    const [filters, setFilters] = useState<AuditLogFilters>({});

    const [items, setItems] = useState<AuditLogType[]>([]);
    const [loading, setLoading] = useState(false);
    const [cursor, setCursor] = useState<AuditLogCursor | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [userNameMap, setUserNameMap] = useState<Record<string, string>>({});
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleCopy = (e: React.MouseEvent, value: string) => {
        e.stopPropagation();
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(value)
            .then(() => {
                setCopiedId(value);
                if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
                copiedTimeoutRef.current = setTimeout(() => setCopiedId(null), 1500);
            })
            .catch((err) => console.error('Clipboard write failed:', err));
    };

    const entityTypes = Object.values(COLLECTIONS).filter(
        c => c !== COLLECTIONS.AUDIT_LOG && c !== COLLECTIONS.NOTIFICATION
    );

    const resolveActors = async (newItems: AuditLogType[]) => {
        const unknown = [...new Set(newItems.map(i => i.actorUid))]
            .filter(uid => uid !== 'system' && !(uid in userNameMap));
        if (unknown.length === 0) return;
        const results = await Promise.all(
            unknown.map(uid => getUser(uid).then(u => ({ uid, name: u?.name ?? uid })))
        );
        const patch: Record<string, string> = {};
        for (const { uid, name } of results) patch[uid] = name;
        setUserNameMap(m => ({ ...m, ...patch }));
    };

    const loadInitial = async (activeFilters: AuditLogFilters) => {
        setLoading(true);
        setItems([]);
        setCursor(null);
        setHasMore(false);
        setExpandedIds(new Set());
        try {
            const result = await getList(activeFilters, { limit: PAGE_SIZE });
            setItems(result.items);
            setCursor(result.cursor);
            setHasMore(result.hasMore);
            resolveActors(result.items).catch(console.error);
        } finally {
            setLoading(false);
        }
    };

    const loadMore = async () => {
        if (!cursor) return;
        setLoading(true);
        try {
            const result = await getList(filters, { limit: PAGE_SIZE, startAfter: cursor });
            setItems(prev => [...prev, ...result.items]);
            setCursor(result.cursor);
            setHasMore(result.hasMore);
            resolveActors(result.items).catch(console.error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadInitial(filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleApply = () => {
        const next: AuditLogFilters = {};
        if (pending.entityType) next.entityType = pending.entityType;
        if (pending.entityId) next.entityId = pending.entityId;
        if (pending.actorUid) next.actorUid = pending.actorUid;
        if (pending.dateFrom) next.dateFrom = new Date(pending.dateFrom);
        if (pending.dateTo) {
            const d = new Date(pending.dateTo);
            d.setHours(23, 59, 59, 999);
            next.dateTo = d;
        }
        setFilters(next);
    };

    const handleReset = () => {
        setPending(EMPTY_PENDING);
        setFilters({});
    };

    const toggleExpand = (id: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    return (
        <Box sx={sx.root}>
            <Typography variant="h4" fontWeight={700} mb={0.5}>{t('auditLog.title')}</Typography>
            <Typography color="text.secondary" mb={3}>{t('auditLog.subtitle')}</Typography>

            {/* Filters */}
            <Paper elevation={0} sx={sx.filtersPaper}>
                <TextField
                    label={t('auditLog.filterDateFrom')}
                    type="date"
                    size="small"
                    value={pending.dateFrom}
                    onChange={e => setPending(p => ({ ...p, dateFrom: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={sx.filterDateField}
                />
                <TextField
                    label={t('auditLog.filterDateTo')}
                    type="date"
                    size="small"
                    value={pending.dateTo}
                    onChange={e => setPending(p => ({ ...p, dateTo: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={sx.filterDateField}
                />
                <FormControl size="small" sx={sx.filterEntityTypeControl}>
                    <InputLabel>{t('auditLog.filterEntityType')}</InputLabel>
                    <Select
                        value={pending.entityType}
                        label={t('auditLog.filterEntityType')}
                        onChange={e => setPending(p => ({ ...p, entityType: e.target.value, entityId: e.target.value ? p.entityId : '' }))}
                    >
                        <MenuItem value=""><em>{t('auditLog.filterAll')}</em></MenuItem>
                        {entityTypes.map(type => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label={t('auditLog.filterEntityId')}
                    size="small"
                    value={pending.entityId}
                    onChange={e => setPending(p => ({ ...p, entityId: e.target.value }))}
                    disabled={!pending.entityType}
                    sx={sx.filterTextField}
                />
                <TextField
                    label={t('auditLog.filterActorUid')}
                    size="small"
                    value={pending.actorUid}
                    onChange={e => setPending(p => ({ ...p, actorUid: e.target.value }))}
                    sx={sx.filterTextField}
                />
                <Button variant="contained" size="small" onClick={handleApply} sx={sx.filterButton}>
                    {t('auditLog.filterApply')}
                </Button>
                <Button variant="outlined" size="small" onClick={handleReset} sx={sx.filterButton}>
                    {t('auditLog.filterReset')}
                </Button>
            </Paper>

            {/* Table */}
            <TableContainer component={Paper} elevation={0} sx={sx.tableContainer}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={sx.tableHeaderRow}>
                            <TableCell width={48} />
                            <TableCell>{t('auditLog.colTimestamp')}</TableCell>
                            <TableCell>{t('auditLog.colActor')}</TableCell>
                            <TableCell>{t('auditLog.colOperation')}</TableCell>
                            <TableCell>{t('auditLog.colEntityType')}</TableCell>
                            <TableCell>{t('auditLog.colEntityId')}</TableCell>
                            <TableCell>{t('auditLog.colAction')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={sx.loadingCell}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={sx.emptyCell}>
                                    {t('auditLog.empty')}
                                </TableCell>
                            </TableRow>
                        )}
                        {items.map(item => {
                            const isExpanded = expandedIds.has(item.id);
                            const hasDetail = !!(item.before || item.after || item.metadata);
                            return (
                                <React.Fragment key={item.id}>
                                    <TableRow hover onClick={() => toggleExpand(item.id)} sx={sx.dataRow}>
                                        <TableCell sx={sx.expandCell}>
                                            <IconButton size="small" tabIndex={-1}>
                                                {isExpanded
                                                    ? <ChevronUp size={16} />
                                                    : <ChevronDown size={16} />
                                                }
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap>
                                                {item.timestamp.toLocaleString('cs-CZ')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {userNameMap[item.actorUid] ?? item.actorUid}
                                            </Typography>
                                            {item.actorUid !== 'system' && (
                                                <Box sx={sx.idRow}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={sx.monoText}
                                                        component={Link}
                                                        to={`/app/admin/users/${item.actorUid}`}
                                                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                    >
                                                        {item.actorUid}
                                                    </Typography>
                                                    <IconButton size="small" onClick={e => handleCopy(e, item.actorUid)} sx={sx.copyButton}>
                                                        {copiedId === item.actorUid ? <Check size={12} /> : <Copy size={12} />}
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                color={OPERATION_COLORS[item.action] ?? 'text.primary'}
                                            >
                                                {item.action}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{item.entityType}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={sx.idRow}>
                                                <Typography variant="body2" sx={sx.monoText}>
                                                    {item.entityId}
                                                </Typography>
                                                <IconButton size="small" onClick={e => handleCopy(e, item.entityId)} sx={sx.copyButton}>
                                                    {copiedId === item.entityId ? <Check size={12} /> : <Copy size={12} />}
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {item.entityAction && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.entityAction}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>

                                    {/* Detail row */}
                                    <TableRow>
                                        <TableCell
                                            colSpan={7}
                                            sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}
                                        >
                                            <Collapse in={isExpanded} unmountOnExit>
                                                <Box sx={sx.detailBox}>
                                                    {item.before && (
                                                        <Box sx={sx.detailColumn}>
                                                            <Typography
                                                                variant="caption"
                                                                fontWeight={700}
                                                                color="error.main"
                                                                display="block"
                                                                mb={0.5}
                                                            >
                                                                {t('auditLog.before')}
                                                            </Typography>
                                                            <JsonBlock data={item.before} />
                                                        </Box>
                                                    )}
                                                    {item.after && (
                                                        <Box sx={sx.detailColumn}>
                                                            <Typography
                                                                variant="caption"
                                                                fontWeight={700}
                                                                color="success.main"
                                                                display="block"
                                                                mb={0.5}
                                                            >
                                                                {t('auditLog.after')}
                                                            </Typography>
                                                            <JsonBlock data={item.after} />
                                                        </Box>
                                                    )}
                                                    {item.metadata && (
                                                        <Box sx={sx.detailColumn}>
                                                            <Typography
                                                                variant="caption"
                                                                fontWeight={700}
                                                                color="text.secondary"
                                                                display="block"
                                                                mb={0.5}
                                                            >
                                                                {t('auditLog.metadata')}
                                                            </Typography>
                                                            <JsonBlock data={item.metadata} />
                                                        </Box>
                                                    )}
                                                    {!hasDetail && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t('auditLog.noDetail')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Load more */}
            {hasMore && (
                <Box sx={sx.loadMoreBox}>
                    <Button
                        variant="outlined"
                        onClick={loadMore}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} /> : undefined}
                    >
                        {loading ? t('auditLog.loadingMore') : t('auditLog.loadMore')}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default AuditLog;
